import { DatePicker, Space } from "antd";
import { Option, ReadonlyArray } from "fp";

import * as Moment from "moment";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useDispatch } from "~/Action";
import { Breadcrumb as GenericBreadcrumb } from "~/Breadcrumb";
import { Button } from "~/ButtonV2";
import * as Driver from "~/Driver";
import * as IndividualDriver from "~/IndividualDriver";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import * as Link from "~/Link";
import * as Loading from "~/Loading";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import * as TimeTracking from "~/TimeTracking";
import * as Typography from "~/Typography";

export const Component = () =>
  pipe(
    Log.Route.useData(),
    Option.map((route) => <Page {...route} />),
    Option.toNullable
  );

const Page = (route: Log.Route) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const log = Remote.toUndefined(Log.Data.useFromRoute(route));

  // Pre-fetch Revisions
  Log.Revision.useFromRoute(route);

  React.useEffect(() => {
    if (log) {
      dispatch(Log.Action.Requested.create(route));
      dispatch(Log.Action.Received.create(log));
    } else {
      dispatch(Log.Action.Clear.create());
    }
  }, [history.location, log && Log.id(log)]);

  React.useEffect(() => {
    if (route.driver.id) {
      dispatch(IndividualDriver.DriverId.Action.Updated.create(Number(route.driver.id)));
      dispatch(IndividualDriver.Action.Requested.create());
    }
  }, [route.driver.id]);

  const driverZone = IndividualDriver.useFromIndividualReference({
    ...route.driver,
    missing: () => undefined,
    found: ({ time }) => time,
  });

  return (
    <Layout.Page.Component all>
      <Layout.Section.Container.Component bottom>
        <Header {...route} />
        <Log.DriverTimezoneMismatchWarning driverZone={driverZone} />
      </Layout.Section.Container.Component>
      <Layout.Section.Container.Component bottom>
        <Log.Form.Component {...route} />
      </Layout.Section.Container.Component>
      <Layout.Section.Container.Component bottom>
        <Log.Timeline.Component {...route} />
      </Layout.Section.Container.Component>
      <Layout.Section.Container.Component bottom>
        <Log.Issue.Component {...route} />
      </Layout.Section.Container.Component>
      <Layout.Section.Container.Component bottom>
        <Log.DVIRs {...route} />
      </Layout.Section.Container.Component>
      <Layout.Section.Container.Component bottom>
        <Log.Event.Component {...route} />
      </Layout.Section.Container.Component>
      <Layout.Section.Container.Component bottom>
        <Log.Revision.Component {...route} />
      </Layout.Section.Container.Component>
      <Layout.Section.Container.Component bottom>
        <Log.Audit.Component {...route} />
      </Layout.Section.Container.Component>
    </Layout.Page.Component>
  );
};

function Header(route: Log.Route) {
  return (
    <Header.Container>
      <Header.Left>
        <Header.Breadcrumb {...route} />
        <Header.TimeTrackingLink {...route} />
      </Header.Left>
      <Header.Center>
        <Typography.H1.Component>
          <DriverName {...route} />
        </Typography.H1.Component>
        <Layout.Block.Component top>
          <Header.Navigation {...route} />
        </Layout.Block.Component>
      </Header.Center>
      <Header.Right>
        <Log.ExportMenu {...route} />
      </Header.Right>
    </Header.Container>
  );
}

function DriverName(route: Log.Route) {
  return route.driver.name ? (
    <Driver.Name.Component {...route.driver} noLink includeInactive/>
  ) : (
    <Loading.Component />
  );
}

namespace Header { 
  export const Container = styled.div`
    display: flex;
    & > div {
      flex: 1;
      display: flex;
    }
  `;

  export const Left = styled.div`
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  `;

  export const Center = styled.div`
    align-items: center;
    justify-content: center;
    flex-direction: column;
  `;

  export const Right = styled.div`
    align-items: center;
    justify-content: flex-end;
  `;

  export const Breadcrumb = (route: Log.Route) => {
    const driver = IndividualDriver.useFromIndividualReference({
      ...route.driver,
      missing: () => undefined,
      found: (driver) => driver,
    });

    return (
      <GenericBreadcrumb>
        <GenericBreadcrumb.Item>
          <Link.Component onClick={Driver.Overview.Route.use().go}>
            Drivers
          </Link.Component>
        </GenericBreadcrumb.Item>
        <GenericBreadcrumb.Item>
          {driver ? (
            <Driver.Name.Component {...driver} />
          ) : (
            <Loading.Component />
          )}
        </GenericBreadcrumb.Item>
        <GenericBreadcrumb.Item>
          {Time.DateTime.toISOLocalWithoutTime(route)}
        </GenericBreadcrumb.Item>
      </GenericBreadcrumb>
    );
  };

  export const TimeTrackingLink = (route: Log.Route) =>
    pipe(
      TimeTracking.Link.useHREF(route),
      Option.map((href) => (
        <Layout.Inline.Component top>
          <Link.Component href={href} target="_blank">
            <Layout.Space>
              View the time card for this day
              <Icon.ExternalLinkSquareAlt />
            </Layout.Space>
          </Link.Component>
        </Layout.Inline.Component>
      )),
      Option.toNullable
    );

  export function Navigation(route: Log.Route) {
    const history = useHistory();

    const middleOfDay = Moment.default(route.dateTime).hour(11);
    const [next, previous] = pipe(
      [
        middleOfDay.valueOf() - Time.Duration.days(1).duration,
        middleOfDay.valueOf() + Time.Duration.days(1).duration,
      ],
      ReadonlyArray.map((dateTime) => ({
        dateTime,
        formatted: Time.DateTime.toISOLocalWithoutTime({ dateTime }),
      }))
    );

    return (
      <Navigation.Container>
        <Button onClick={Log.Route.use({ ...route, ...next }).go}>
          <Space>
            <Icon.ArrowLeft />
            {next.formatted}
          </Space>
        </Button>
        <Layout.Inline.Component left right>
          <DatePicker
            placeholder=""
            allowClear={false}
            value={Moment.default(route.dateTime)}
            onChange={(moment) =>
              moment &&
              history.push(
                Log.Route.href({
                  ...route,
                  ...Time.DateTime.fromNumber(moment.valueOf()),
                })
              )
            }
          />
        </Layout.Inline.Component>
        <Button onClick={Log.Route.use({ ...route, ...previous }).go}>
          <Space>
            {previous.formatted}
            <Icon.ArrowRight />
          </Space>
        </Button>
      </Navigation.Container>
    );
  }

  export namespace Navigation {
    export const Container = styled.div`
      display: flex;
      flex-wrap: nowrap;
    `;
  }
}
